/**
 * Centralized Error Handler
 * 
 * Systematically handles errors, warnings, and other events by converting them to toast notifications.
 * Provides global error handlers and utilities for consistent error handling across the app.
 */

import { bus } from '../../core/events.js';
import { log } from '../logging/index.js';

export type ErrorSeverity = 'error' | 'warning' | 'info' | 'success';

export interface ErrorNotification {
  message: string;
  severity: ErrorSeverity;
  duration?: number; // Auto-dismiss duration in ms (default: 5000 for errors, 3000 for warnings/info)
  action?: {
    label: string;
    handler: () => void;
  };
}

/**
 * Show an error notification toast
 */
export function showErrorNotification(
  message: string,
  options?: {
    severity?: ErrorSeverity;
    duration?: number;
    action?: { label: string; handler: () => void };
  }
): void {
  const severity = options?.severity || 'error';
  
  // Map severity to notification type
  const typeMap: Record<ErrorSeverity, 'error' | 'warning' | 'info' | 'success'> = {
    error: 'error',
    warning: 'warning',
    info: 'info',
    success: 'success'
  };
  
  const notification = {
    type: typeMap[severity],
    message,
    duration: options?.duration !== undefined 
      ? options.duration 
      : severity === 'error' ? 5000 : severity === 'warning' ? 3000 : 3000,
    action: options?.action ? {
      label: options.action.label,
      onClick: options.action.handler
    } : undefined
  };

  console.log(`[Error Handler E2E] ERROR NOTIFICATION: severity=${severity}, message="${message}", duration=${notification.duration}ms`);
  
  // Emit notification event (notification process will handle it)
  console.log(`[Error Handler E2E] → Emitting notification.show event`);
  bus.emit('notification.show', notification);
  console.log(`[Error Handler E2E] → Event emitted`);
  
  // Also emit error event for metaball (if severity is error)
  if (severity === 'error') {
    console.log(`[Error Handler E2E] → Emitting error event for metaball`);
    bus.emit('error.occurred' as any, {
      message,
      severity,
      timestamp: Date.now(),
      source: 'error-handler'
    });
  }
  
  // Also log the error
  if (severity === 'error') {
    log.error(`[ErrorHandler] ${message}`);
  } else if (severity === 'warning') {
    log.warn(`[ErrorHandler] ${message}`);
  } else {
    log.info(`[ErrorHandler] ${message}`);
  }
}

/**
 * Handle fetch errors (network errors, HTTP errors, etc.)
 */
export function handleFetchError(error: unknown, context?: string): void {
  console.log(`[Error Handler E2E] FETCH ERROR: context=${context || 'unknown'}, error=`, error);
  
  // Send to Sentry (async, don't block)
  import('../sentry/index.js').then(({ captureException }) => {
    captureException(error, { context: context || 'unknown' });
  }).catch(() => {
    // Sentry not available, continue without it
  });
  
  let message = 'An error occurred';
  
  if (error instanceof TypeError && error.message.includes('fetch')) {
    // Network error (offline, CORS, etc.)
    message = 'Network error: Unable to connect to server. Please check your internet connection.';
    console.log(`[Error Handler E2E] → Network error detected: ${error.message}`);
  } else if (error instanceof Error) {
    message = error.message;
    console.log(`[Error Handler E2E] → Error object: ${error.message}`);
  } else if (typeof error === 'string') {
    message = error;
    console.log(`[Error Handler E2E] → String error: ${error}`);
  }
  
  if (context) {
    message = `${context}: ${message}`;
  }
  
  console.log(`[Error Handler E2E] → Calling showErrorNotification: "${message}"`);
  showErrorNotification(message, { severity: 'error' });
}

/**
 * Handle HTTP response errors
 * Returns the error message (extracted from response if possible)
 * Note: This function will consume the response body, so clone it first if you need to read it again
 */
export async function handleHTTPError(response: Response, context?: string): Promise<string> {
  let message = `HTTP ${response.status}`;
  
  // Clone response so we can read it without consuming the original
  const clonedResponse = response.clone();
  
  // Try to extract error message from response
  try {
    const data = await clonedResponse.json();
    if (data.error) {
      message = data.error;
    } else if (data.message) {
      message = data.message;
    }
  } catch {
    // If JSON parsing fails, use status text
    message = response.statusText || `HTTP ${response.status}`;
  }
  
  const fullMessage = context ? `${context}: ${message}` : message;
  const severity = response.status >= 500 ? 'error' : 'warning';
  
  // Send to Sentry for 5xx errors (async, don't block)
  if (response.status >= 500) {
    import('../sentry/index.js').then(({ captureException }) => {
      captureException(new Error(fullMessage), { 
        context: context || 'unknown',
        status: response.status,
        url: response.url,
      });
    }).catch(() => {
      // Sentry not available, continue without it
    });
  }
  
  console.log(`[Error Handler E2E] HTTP ERROR: status=${response.status}, context=${context || 'unknown'}, message="${message}"`);
  console.log(`[Error Handler E2E] → Calling showErrorNotification: severity=${severity}, message="${fullMessage}"`);
  
  showErrorNotification(fullMessage, { 
    severity: severity,
    action: response.status >= 500 ? {
      label: 'Retry',
      handler: () => {
        // Emit retry event - caller should listen for this
        bus.emit('error.retry', { context });
      }
    } : undefined
  });
  
  return message; // Return the error message for caller to use
}

/**
 * Wrapper for fetch that automatically handles errors
 */
export async function fetchWithErrorHandling(
  url: string | URL,
  options?: RequestInit,
  context?: string
): Promise<Response> {
  try {
    const response = await fetch(url, options);
    
    if (!response.ok) {
      await handleHTTPError(response, context);
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    return response;
  } catch (error) {
    // Only handle fetch errors (network errors), not HTTP errors (already handled above)
    if (error instanceof TypeError && error.message.includes('fetch')) {
      handleFetchError(error, context);
    }
    throw error;
  }
}

/**
 * Initialize global error handlers
 */
export function initGlobalErrorHandlers(): void {
  // Handle unhandled errors
  window.addEventListener('error', (event) => {
    // Don't show notification for errors we've already handled
    if (event.error?.handled) {
      return;
    }
    
    const message = event.error?.message || event.message || 'An unexpected error occurred';
    
    // Suppress ResizeObserver loop warnings (harmless browser quirk)
    if (typeof message === 'string' && message.includes('ResizeObserver loop')) {
      return;
    }
    
    // Send to Sentry (async, don't block)
    import('../sentry/index.js').then(({ captureException }) => {
      captureException(event.error || new Error(message));
    }).catch(() => {
      // Sentry not available, continue without it
    });
    
    showErrorNotification(message, { severity: 'error' });
  });
  
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    // Don't show notification for rejections we've already handled
    if ((event.reason as any)?.handled) {
      return;
    }
    
    let message = 'An unexpected error occurred';
    
    if (event.reason instanceof Error) {
      message = event.reason.message;
    } else if (typeof event.reason === 'string') {
      message = event.reason;
    } else if (event.reason?.error) {
      message = event.reason.error;
    }
    
    // Send to Sentry (async, don't block)
    import('../sentry/index.js').then(({ captureException }) => {
      captureException(event.reason instanceof Error ? event.reason : new Error(message));
    }).catch(() => {
      // Sentry not available, continue without it
    });
    
    // Check if it's a fetch error
    if (event.reason instanceof TypeError && event.reason.message.includes('fetch')) {
      handleFetchError(event.reason);
    } else {
      showErrorNotification(message, { severity: 'error' });
    }
  });
  
  log.debug('[ErrorHandler] Global error handlers initialized');
}

